import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Button,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { k8sApi } from '@common-reducers/backend-api/backend-k8s-api';
import PodDetailsDrawer from './k8s-analyzer-sidebar';
import { K8sAnalyzerTopBar } from './k8s-analyzer-topbar';
import ConfirmationDialog from './k8s-analyzer-confirmation-dialog';
import PodTableRow from './k8s-analyzer-pod-table-row';
import { K8sAnalyzerRefreshPodsPanel } from './k8s-analyzer-refresh-pods-panel';
import { K8sAnalyzerDeletePodsPanel, isPodDeletionCandidate } from './k8s-analyzer-delete-pods-panel';
import { K8sAnalyzerPodHistoryPanel } from './k8s-analyzer-pod-history-panel';
import { K8sAnalyzerNodeStatusAccordion } from './k8s-analyzer-node-status-accordion';
import { PodStatusSummary } from './pod-status-summary';
import { SessionProps } from './session-details-cube';
import { History, ExpandMore } from '@mui/icons-material';
import { ContainerState, ContainerStatus, K8sPod, NodeResources, NodeStats, FilterState, K8sApiResponse } from './k8s-analyzer-types';

const AVAILABLE_STATUSES = [
  'Running',
  'Completed',
  'ContainerStatusUnknown',
  'Error',
  'Error↓',
  'OOMKilled',
  'CrashLoopBackOff',
  'Terminating'
];

const SESSION_STATUSES = [
  'disconnected',
  'initializing',
  'browser_started',
  'navigating',
  'unpaired',
  'qrcode',
  'connected'
];

const KNOWN_MACHINE_TYPES = ['e2-highmem-8', 'e2-standard', 'e2-highmem-4'];

export const extractMachineType = (nodeName: string | undefined): string => {
  if (!nodeName) {
    console.log(`Pod has no node name assigned: ${nodeName}`);
    return 'Unknown';
  }

  const matchedType = KNOWN_MACHINE_TYPES.find(type => nodeName.includes(type));
  if (matchedType) {
    return matchedType;
  }

  // Special case for serenix-eks-blue nodes
  if (nodeName.includes('serenix-eks-blue')) {
    return 'e2-highmem-4';
  }
  console.log('Unknown machine type for node:', nodeName);
  return 'Unknown';
};

export function K8sAnalyzer() {
  const [getK8SGatewayPods, { isLoading: apiIsLoading, error: apiError }] = k8sApi.useGetK8SGatewayPodsWithRelatedSessionsActionMutation();
  const [deletePod] = k8sApi.useDeletePodByIdActionMutation();
  const [pods, setPods] = useState<K8sPod[]>([]);
  const [deploymentTypeMap, setDeploymentTypeMap] = useState<any>(null);
  const [sessionsWithoutPods, setSessionsWithoutPods] = useState<SessionProps[]>([]);
  const [sessionsWithoutUsers, setSessionsWithoutUsers] = useState<SessionProps[]>([]);
  const [sessionStatusCounts, setSessionStatusCounts] = useState<Record<string, number>>({});
  const [allSessions, setAllSessions] = useState<SessionProps[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedPod, setSelectedPod] = useState<K8sPod | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    podName: '',
    statuses: [],
    sessionStatus: '',
    userEmail: '',
    showDeletionCandidates: false
  });
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [podsToDelete, setPodsToDelete] = useState<K8sPod[]>([]);
  const [isPodHistoryOpen, setIsPodHistoryOpen] = useState(false);
  const [podStatusChangeCount, setPodStatusChangeCount] = useState<number>(0);
  const [nodeTypeStats, setNodeTypeStats] = useState<Record<string, Record<string, number>>>({});
  const [nodeResources, setNodeResources] = useState<Record<string, NodeResources>>({});
  const [nodeStats, setNodeStats] = useState<Record<string, NodeStats>>({});

  // Check if there are any non-running pods
  const nonRunningPodsCount = pods.filter(pod =>
    pod.statusString !== 'Running' && ['Error', 'OOMKilled', 'ContainerStatusUnknown', 'Completed'].includes(pod.statusString)
  ).length;

  const calculateNodeTypeStats = (pods: K8sPod[]) => {
    const nodeStats: Record<string, NodeStats> = {};
    const typeStats: Record<string, Record<string, number>> = {};

    pods.forEach(pod => {
      const nodeName = pod.nodeName || 'Unknown';
      const nodeType = extractMachineType(pod.nodeName);

      // Initialize node stats if not exists
      if (!nodeStats[nodeName]) {
        nodeStats[nodeName] = {
          nodeName,
          nodeType,
          pods: {
            total: 0,
            Running: 0,
            ContainerStatusUnknown: 0,
            OOMKilled: 0,
            Completed: 0,
            Error: 0,
            'Error↓': 0,
            CrashLoopBackOff: 0,
            Terminating: 0
          },
          resources: {
            cpu: {
              total: nodeType.includes('highmem-8') ? 8 : nodeType.includes('highmem-4') ? 4 : 2,
              used: 0,
              unit: 'cores'
            },
            memory: {
              total: nodeType.includes('highmem-8') ? 60 : nodeType.includes('highmem-4') ? 30 : 8,
              used: 0,
              unit: 'Gi'
            }
          }
        };
      }

      // Update node stats
      nodeStats[nodeName].pods[pod.statusString] = (nodeStats[nodeName].pods[pod.statusString] || 0) + 1;
      nodeStats[nodeName].pods.total += 1;
      nodeStats[nodeName].resources.cpu.used += 1; // Assuming 1 core per pod
      nodeStats[nodeName].resources.memory.used += 2; // Assuming 2GB per pod

      // Also maintain type stats for backward compatibility
      if (!typeStats[nodeType]) {
        typeStats[nodeType] = {
          Running: 0,
          ContainerStatusUnknown: 0,
          OOMKilled: 0,
          Completed: 0,
          Error: 0,
          total: 0
        };
      }
      typeStats[nodeType][pod.statusString] = (typeStats[nodeType][pod.statusString] || 0) + 1;
      typeStats[nodeType].total += 1;
    });

    setNodeTypeStats(typeStats);
    setNodeStats(nodeStats);
  };

  const fetchPodStatus = useCallback(async (showButtonLoader = false) => {
    if (showButtonLoader) {
      setIsRefreshing(true);
    }

    try {
      console.log('Fetching pod status...');
      const response = await getK8SGatewayPods().unwrap() as K8sApiResponse;
      console.log('Pod status response:', response);

      if (!response || !response.pods || response.pods.length === 0) {
        setError('No pods found. Backend may not be connected to Kubernetes.');
        return;
      }

      // Store deployment type map
      if (response.deploymentTypeMap) {
        setDeploymentTypeMap(response.deploymentTypeMap);
      }

      // Store sessions without pods and users
      setSessionsWithoutPods(response.sessionsWithoutPods || []);
      setSessionsWithoutUsers(response.sessionsWithoutUsers || []);

      // Transform pods into our simplified format
      const validatedPods: K8sPod[] = response.pods.map(pod => {
        const containerStatuses = (pod.status?.containerStatuses || []).map(cs => {
          // Convert string state to our ContainerState interface
          const state: ContainerState = {
            running: cs.state === 'Running' ? { startedAt: undefined } : undefined,
            waiting: cs.state === 'Waiting' ? { reason: cs.state } : undefined,
            terminated: cs.state === 'Terminated' ? { reason: cs.state } : undefined
          };

          return {
            containerID: cs.containerID || '',
            ready: cs.ready || false,
            restartCount: cs.restartCount || 0,
            state
          };
        });

        // Use detailedStatus if available, otherwise fallback to phase
        const statusString = pod.detailedStatus?.detailedStatus || pod.status?.phase || 'Unknown';
        const readyState = pod.detailedStatus?.readyState || pod.status?.readyState || 'Unknown';

        return {
          metadata: {
            name: pod.metadata?.name || 'Unknown',
            namespace: pod.metadata?.namespace || 'default',
            labels: {
              'app.kubernetes.io/instance': pod.metadata?.labels?.['app.kubernetes.io/instance'] || '',
              'app.kubernetes.io/name': pod.metadata?.labels?.['app.kubernetes.io/name'] || '',
              version: pod.metadata?.labels?.version || 'Unknown'
            }
          },
          status: {
            phase: pod.status?.phase || 'Unknown',
            containerStatuses: containerStatuses,
            conditions: pod.status?.conditions || []
          },
          detailedStatus: pod.detailedStatus || {
            name: pod.metadata?.name || 'Unknown',
            detailedStatus: statusString,
            phase: pod.status?.phase || 'Unknown',
            readyState: readyState
          },
          name: pod.metadata?.name || 'Unknown',
          nodeName: pod.spec?.nodeName || pod.nodeName || '',
          readyState: readyState,
          version: pod.metadata?.labels?.version || 'Unknown',
          containerStatuses: containerStatuses,
          sessions: pod.sessions || [],
          nodeMachineType: pod.spec?.nodeMachineType || pod.nodeMachineType || 'Unknown',
          statusString: statusString
        };
      });

      setPods(validatedPods);
      calculateNodeTypeStats(validatedPods);

      // Calculate node resources
      const resources: Record<string, NodeResources> = {};
      validatedPods.forEach(pod => {
        const nodeType = extractMachineType(pod.nodeName);
        if (!resources[nodeType]) {
          // Initialize resources for this node type
          resources[nodeType] = {
            cpu: {
              total: 0,
              used: 0,
              unit: 'cores'
            },
            memory: {
              total: 0,
              used: 0,
              unit: 'Gi'
            }
          };
        }
        
        // Add pod resources
        resources[nodeType].cpu.used += 1; // Assuming 1 core per pod
        resources[nodeType].memory.used += 2; // Assuming 2GB per pod
      });

      // Set total resources based on node type
      Object.keys(resources).forEach(nodeType => {
        if (nodeType.includes('highmem-8')) {
          resources[nodeType].cpu.total = 8;
          resources[nodeType].memory.total = 60;
        } else if (nodeType.includes('highmem-4')) {
          resources[nodeType].cpu.total = 4;
          resources[nodeType].memory.total = 30;
        } else if (nodeType.includes('standard')) {
          resources[nodeType].cpu.total = 2;
          resources[nodeType].memory.total = 8;
        }
      });

      setNodeResources(resources);

      // Calculate session status counts from all sessions
      const statusCounts: Record<string, number> = {};
      const countedSessionIds = new Set<string>();
      const collectedSessions: SessionProps[] = [];

      // Initialize counts for all session statuses to zero
      SESSION_STATUSES.forEach(status => {
        statusCounts[status] = 0;
      });

      // Count sessions from pods
      validatedPods.forEach(pod => {
        if (pod.sessions && pod.sessions.length > 0) {
          pod.sessions.forEach(session => {
            if (session.status && !countedSessionIds.has(session._id)) {
              statusCounts[session.status] = (statusCounts[session.status] || 0) + 1;
              countedSessionIds.add(session._id);
              collectedSessions.push(session);
            }
          });
        }
      });

      // Count sessions without pods
      if (response.sessionsWithoutPods) {
        response.sessionsWithoutPods.forEach(session => {
          if (session.status && !countedSessionIds.has(session._id)) {
            statusCounts[session.status] = (statusCounts[session.status] || 0) + 1;
            countedSessionIds.add(session._id);
            collectedSessions.push(session);
          }
        });
      }

      // Count sessions without users
      if (response.sessionsWithoutUsers) {
        response.sessionsWithoutUsers.forEach(session => {
          if (session.status && !countedSessionIds.has(session._id)) {
            statusCounts[session.status] = (statusCounts[session.status] || 0) + 1;
            countedSessionIds.add(session._id);
            collectedSessions.push(session);
          }
        });
      }

      // Add count for all sessions if available
      if (response.allSessions) {
        response.allSessions.forEach(session => {
          if (session.status && !countedSessionIds.has(session._id)) {
            statusCounts[session.status] = (statusCounts[session.status] || 0) + 1;
            countedSessionIds.add(session._id);
            collectedSessions.push(session);
          }
        });
      }

      setSessionStatusCounts(statusCounts);
      setAllSessions(collectedSessions);
      setError(null);
    } catch (err) {
      console.error('Error fetching pod status:', err);
      const errorMessage = err?.data?.message || err?.message || 'Failed to fetch pod status information';
      setError(`${errorMessage}. Backend may not be connected to Kubernetes.`);
    } finally {
      if (showButtonLoader) {
        setIsRefreshing(false);
      }
    }
  }, [getK8SGatewayPods]);

  // Create a promisified version of fetchPodStatus for the DeletePodsPanel
  const refreshPodsAsync = useCallback(async () => {
    return fetchPodStatus(false);
  }, [fetchPodStatus]);

  // Initial fetch on component mount
  useEffect(() => {
    fetchPodStatus(true);
  }, []);

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await Promise.all(podsToDelete.map(pod => deletePod(pod.name)));
      await fetchPodStatus();
    } catch (err) {
      setError(`Failed to delete filtered pods: ${err?.data?.message || err?.message || 'Unknown error'}`);
    } finally {
      setIsDeleting(false);
      setIsConfirmationOpen(false);
    }
  };

  const handleCancelDelete = () => {
    setIsConfirmationOpen(false);
    setPodsToDelete([]);
  };

  const handleViewPodDetails = (pod: K8sPod) => {
    setSelectedPod(pod);
  };

  const handlePodDeleted = () => {
    // Refresh the pod list after a pod has been deleted
    fetchPodStatus();
  };

  const filteredPods = pods.filter(pod => {
    // If showing deletion candidates, only show pods that would be deleted by "Delete Filtered Non-Running Pods"
    if (filters.showDeletionCandidates) {
      return isPodDeletionCandidate(pod);
    }

    // Normal filtering
    const nameMatch = pod.name.toLowerCase().includes(filters.podName.toLowerCase());
    const statusMatch = filters.statuses.length === 0 || filters.statuses.includes(pod.statusString);

    // Session status filter
    const sessionStatusMatch = !filters.sessionStatus ||
      (pod.sessions?.some(session => session.status === filters.sessionStatus) ?? false);

    // User email, session ID, first name, and last name filter
    const userFilter = !filters.userEmail ||
      (pod.sessions?.some(session =>
        (session.user?.email?.toLowerCase().includes(filters.userEmail.toLowerCase())) ||
        (session._id?.toLowerCase().includes(filters.userEmail.toLowerCase())) ||
        (session.user?.firstName?.toLowerCase().includes(filters.userEmail.toLowerCase())) ||
        (session.user?.lastName?.toLowerCase().includes(filters.userEmail.toLowerCase()))
      ) ?? false);

    return nameMatch && statusMatch && sessionStatusMatch && userFilter;
  });

  // Add a method to toggle the pod history panel
  const togglePodHistoryPanel = () => {
    setIsPodHistoryOpen(!isPodHistoryOpen);
  };

  if (apiIsLoading && !isRefreshing && pods.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, margin: '0 auto' }}>
      {deploymentTypeMap && (
        <>
          <K8sAnalyzerTopBar
            deploymentTypeMap={deploymentTypeMap}
            sessionsWithoutPods={sessionsWithoutPods}
            sessionsWithoutUsers={sessionsWithoutUsers}
            sessionStatusCounts={sessionStatusCounts}
            allSessions={allSessions}
            pods={pods}
          />
          {/* <K8sAnalyzerNodeStatusAccordion nodeStats={nodeStats} /> */}
          <Accordion defaultExpanded sx={{ mb: 2 }}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="pod-status-summary-content"
              id="pod-status-summary-header"
            >
              <Typography variant="h6">Pod Status Summary</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <PodStatusSummary nodeStats={nodeStats} />
            </AccordionDetails>
          </Accordion>
        </>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4">
          Gateway Pods Status
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <K8sAnalyzerRefreshPodsPanel
            onRefresh={() => fetchPodStatus(true)}
            isRefreshing={isRefreshing}
          />

          <K8sAnalyzerDeletePodsPanel
            pods={pods}
            onPodsDeleted={handlePodDeleted}
            setIsConfirmationOpen={setIsConfirmationOpen}
            setPodsToDelete={setPodsToDelete}
            setError={setError}
            onRefresh={() => fetchPodStatus(true)}
          />

          <Badge
            badgeContent={podStatusChangeCount}
            color="warning"
            showZero={false}
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.75rem',
                height: '20px',
                minWidth: '20px',
              }
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={togglePodHistoryPanel}
              startIcon={<History />}
            >
              Pod History
            </Button>
          </Badge>
        </Box>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Filter by Pod Name"
          variant="outlined"
          size="small"
          value={filters.podName}
          onChange={(e) => setFilters(prev => ({ ...prev, podName: e.target.value }))}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Status</InputLabel>
          <Select
            multiple
            value={filters.statuses}
            onChange={(e) => setFilters(prev => ({ ...prev, statuses: typeof e.target.value === 'string' ? [] : e.target.value }))}
            input={<OutlinedInput label="Filter by Status" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {AVAILABLE_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Session Status</InputLabel>
          <Select
            value={filters.sessionStatus}
            onChange={(e) => setFilters(prev => ({ ...prev, sessionStatus: e.target.value }))}
            input={<OutlinedInput label="Filter by Session Status" />}
            displayEmpty
          >
            <MenuItem value=""></MenuItem>
            {SESSION_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Filter by User/Session"
          variant="outlined"
          size="small"
          value={filters.userEmail}
          onChange={(e) => setFilters(prev => ({ ...prev, userEmail: e.target.value }))}
          sx={{ minWidth: 250 }}
          placeholder="Search by email, session ID, name"
        />
        <FormControlLabel
          control={
            <Switch
              checked={filters.showDeletionCandidates}
              onChange={(e) => setFilters(prev => ({ ...prev, showDeletionCandidates: e.target.checked }))}
              color="warning"
            />
          }
          label={
            <Typography variant="body2" color={filters.showDeletionCandidates ? "warning.main" : "text.primary"}>
              Show deletion candidates
            </Typography>
          }
          sx={{
            border: filters.showDeletionCandidates ? '1px solid' : 'none',
            borderColor: 'warning.main',
            borderRadius: 1,
            px: filters.showDeletionCandidates ? 1 : 0,
            backgroundColor: filters.showDeletionCandidates ? 'warning.light' : 'transparent'
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {filteredPods.length === 0 && !error && (
        <Alert severity="info" sx={{ mb: 4 }}>
          {pods.length === 0 ? (
            <>
              No gateway pods found. This could mean:
              1. No gateway pods are currently running
              2. You don't have permission to view pods
              3. The backend service is not properly connected to Kubernetes
              4. The backend service's Kubernetes configuration is missing or invalid
            </>
          ) : (
            filters.showDeletionCandidates ?
              'No pods match the deletion criteria (non-running pods without sessions)' :
              'No pods match the current filters'
          )}
        </Alert>
      )}

      {filters.showDeletionCandidates && filteredPods.length > 0 && (
        <Alert severity="warning" sx={{ mb: 4 }}>
          Showing {filteredPods.length} pods that will be deleted by the "Delete Filtered Non-Running Pods" action.
          These are pods that are not running (Error, OOMKilled, or ContainerStatusUnknown) and have no active sessions.
        </Alert>
      )}

      {filteredPods.length > 0 && (
        <TableContainer component={Paper}>
          <Table sx={{ width: '100%' }} aria-label="gateway pods table">
            <TableHead>
              <TableRow>
                <TableCell>Pod Name</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Ready State</TableCell>
                <TableCell align="center">Version</TableCell>
                <TableCell>Container Status</TableCell>
                <TableCell>Sessions</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPods.map((pod) => (
                <PodTableRow
                  key={pod.name}
                  pod={pod}
                  onViewDetails={handleViewPodDetails}
                  onPodDeleted={handlePodDeleted}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <PodDetailsDrawer
        pod={selectedPod}
        open={!!selectedPod}
        onClose={() => setSelectedPod(null)}
      />

      <K8sAnalyzerPodHistoryPanel
        pods={pods}
        open={isPodHistoryOpen}
        onClose={() => setIsPodHistoryOpen(false)}
        onLogCountChange={setPodStatusChangeCount}
      />

      <ConfirmationDialog
        open={isConfirmationOpen}
        title="Confirm Pod Deletion"
        content={`Are you sure you want to delete these ${podsToDelete.length} pods?\n\nPods to delete:\n\n${podsToDelete.map(pod => `• ${pod.name}\n    Status: ${pod.statusString}`).join('\n\n')}`}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
}

export default K8sAnalyzer;